import React from "react";
import { CheckCircle, Briefcase, FileText, Users } from "lucide-react";
import RegisterForm from "./RegisterForm";
import Footer from "../layout/Footer";
import ScrollToTop from "../common/ScrollToTop";
import { useLanguage } from "../../contexts/LanguageContext";
import { useSections } from "../../hooks/useSections";
import { useSiteInfo } from "../../hooks/useSiteInfo";

const pickText = (language, t, en, ja, key) =>
  (language === "ja" && ja) ? ja : (en ?? t(key));

const JobSeekerHeroSection = ({ sectionData, t, language }) => {
  const highlights = [
    { icon: Briefcase, key: "jobs" },
    { icon: Users, key: "support" },
    { icon: FileText, key: "visa" },
    { icon: CheckCircle, key: "free" },
  ];

  return (
    <section className="pt-28 md:pt-36 pb-10 bg-slate-900 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            "linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(to right, #ffffff 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-4 bg-accent-green rounded-full"></div>
          <span className="text-[10px] font-black text-accent-green uppercase tracking-[0.3em]">
            {pickText(language, t, sectionData?.labelEn, sectionData?.labelJa, "jobSeeker.sectionLabel")}
          </span>
        </div>

        <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight tracking-tight mb-4 max-w-3xl">
          {pickText(language, t, sectionData?.titleEn, sectionData?.titleJa, "jobSeeker.hero.title")}
        </h1>
        <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mb-8">
          {pickText(language, t, sectionData?.subtitleEn, sectionData?.subtitleJa, "jobSeeker.hero.subtitle")}
        </p>

        <div className="flex flex-wrap gap-3">
          {highlights.map(({ icon: Icon, key }) => (
            <div
              key={key}
              className="flex items-center gap-2 bg-white/10 border border-white/15 rounded-lg px-3 py-2"
            >
              <Icon className="w-4 h-4 text-accent-green" />
              <span className="text-white text-xs font-bold">
                {t(`jobSeeker.highlights.${key}`)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const JobSeekerStatsSection = ({ sectionData, t, language, info }) => {
  const stats = [
    {
      value: info("apply_stat_placed", info("stat_talent_placed", "1,500+")),
      labelKey: "jobSeeker.stats.placed",
    },
    {
      value: info("apply_stat_rate", info("stat_success_rate", "94%")),
      labelKey: "jobSeeker.stats.rate",
    },
    {
      value: info("apply_stat_match_time", "48h"),
      labelKey: "jobSeeker.stats.matchTime",
    },
    {
      value: info("apply_stat_countries", "15+"),
      labelKey: "jobSeeker.stats.countries",
    },
  ];

  return (
    <div className="bg-subidha-blue py-4 border-y border-white/5">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-3">
          <div className="flex items-center gap-2">
            <div className="w-1 h-3 bg-accent-green rounded-full"></div>
            <span className="text-[9px] font-black uppercase tracking-[0.22em] text-accent-green">
              {pickText(language, t, sectionData?.labelEn, sectionData?.labelJa, "jobSeeker.sectionLabel")}
            </span>
          </div>
          {sectionData?.titleEn || sectionData?.titleJa ? (
            <p className="text-white text-sm md:text-base font-bold mt-1.5">
              {pickText(language, t, sectionData?.titleEn, sectionData?.titleJa, "jobSeeker.hero.title")}
            </p>
          ) : null}
          {sectionData?.subtitleEn || sectionData?.subtitleJa ? (
            <p className="text-slate-300 text-xs md:text-sm mt-1">
              {pickText(language, t, sectionData?.subtitleEn, sectionData?.subtitleJa, "jobSeeker.hero.subtitle")}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-x-10 gap-y-2 items-center justify-center md:justify-start">
          {stats.map(({ value, labelKey }) => (
            <div key={labelKey} className="flex items-baseline gap-2">
              <span className="text-accent-green font-black text-lg">{value}</span>
              <span className="text-slate-300 text-[11px] font-medium uppercase tracking-wider">
                {t(labelKey)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const APPLY_SECTION_REGISTRY = {
  "job-seeker-hero": (sd, utils) => (
    <JobSeekerHeroSection sectionData={sd} t={utils.t} language={utils.language} />
  ),
  "job-seeker-stats": (sd, utils) => (
    <JobSeekerStatsSection
      sectionData={sd}
      t={utils.t}
      language={utils.language}
      info={utils.info}
    />
  ),
  "register-form": (sd) => <RegisterForm sectionData={sd} />,
};

const APPLY_FALLBACK_ORDER = ["job-seeker-hero", "job-seeker-stats", "register-form"];

const JobSeekerPage = () => {
  const { t, language } = useLanguage();
  const { sections, loaded } = useSections("apply");
  const { info } = useSiteInfo();

  const slugsToRender =
    sections.length > 0
      ? sections.filter((s) => s.isActive).map((s) => s.slug)
      : APPLY_FALLBACK_ORDER;

  if (!loaded) {
    return (
      <>
        <div className="min-h-[45vh] bg-slate-900 animate-pulse" />
        <div className="h-20 bg-subidha-blue animate-pulse" />
        <div className="min-h-[40vh] bg-slate-50 animate-pulse" />
      </>
    );
  }

  return (
    <>
      {slugsToRender.map((slug) => {
        const renderer = APPLY_SECTION_REGISTRY[slug];
        if (!renderer) return null;
        const sectionData = sections.find((s) => s.slug === slug) ?? null;
        return (
          <React.Fragment key={slug}>
            {renderer(sectionData, { t, language, info })}
          </React.Fragment>
        );
      })}

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default JobSeekerPage;
